import {useMediaQuery} from '@chakra-ui/react';
import {isEmpty} from 'lodash';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
  UiToggleButton,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {QUERY_MOBILE} from '~/constants';
import {HOT_FLASHES_TREATMENT_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const ENTER_MY_OWN = 'ENTER_MY_OWN_DESCRIPTION';
const NO_SYMPTOMS = 'NO_SYMPTOMS';

const Questionnaire4Screen = () => {
  const dispatch: AppDispatch = useDispatch();

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[HOT_FLASHES_TREATMENT_KEYS.SELECT_SYMPTOMS]?.data,
  );

  const {questionnaires} = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaire = questionnaires?.find(i => i.key === HOT_FLASHES_TREATMENT_KEYS.SELECT_SYMPTOMS);

  const rules = {
    symptomOther: (attributes: any) => {
      return {
        presence: {allowEmpty: false, message: `^This field can't be blank`},
        length: {
          minimum: 5,
          maximum: 300,
          tooShort: '^This field is too short (minimum is %{count} characters)',
          tooLong: '^This field is too long (maximum is %{count} characters)',
          tokenizer: function (value: any) {
            return trimSpaces(value);
          },
        },
      };
    },
  };

  const defaultOptions = questionnaire?.options?.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] =
      !currentAnswer?.selectedOptions?.length && cur?.defaultSelection && currentAnswer?.symptomOther !== NO_SYMPTOMS
        ? cur.defaultSelection
        : currentAnswer?.selectedOptions?.includes(cur.value)
        ? true
        : false;
    return res;
  }, {});

  const resetOptions = questionnaire?.options?.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    return res;
  }, {});

  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      defaultOptions,
      selectedOptions: currentAnswer?.selectedOptions || [],
      selectedSymptomOther: currentAnswer?.selectedSymptomOther || '',
      symptomOther: currentAnswer?.symptomOther || '',
    },
    {rules},
  );

  const dataSubmit =
    inputs?.defaultOptions && Object.keys(inputs.defaultOptions).filter(i => inputs?.defaultOptions?.[i] === true);

  const isDataSubmitValid = dataSubmit && dataSubmit.length > 0;
  const isSymptomOtherValid = inputs.selectedSymptomOther === NO_SYMPTOMS || !!inputs.symptomOther;
  const isValid =
    (isDataSubmitValid && !inputs.selectedSymptomOther) ||
    (isDataSubmitValid && isSymptomOtherValid) ||
    isSymptomOtherValid;

  const onClickOption = (answer: {value: string | number; label: string}) => {
    const {value} = answer;
    setInputs({defaultOptions: {...inputs.defaultOptions, [value]: !inputs?.defaultOptions?.[value]}});

    if (inputs.selectedSymptomOther === NO_SYMPTOMS) {
      setInputs({selectedSymptomOther: ''});
    }
  };

  const onClickSelectedSymptomOther = (answer: {value: any; label: string}) => {
    const {value} = answer;
    if (value === NO_SYMPTOMS) {
      setInputs({defaultOptions: resetOptions, selectedOptions: [], selectedSymptomOther: value, symptomOther: ''});
    } else {
      setInputs({selectedSymptomOther: value, symptomOther: ''});
    }
  };

  const onClickSubmit = async () => {
    if (inputs.selectedSymptomOther === ENTER_MY_OWN) {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    let symptomOther = '';
    if (inputs.selectedSymptomOther === NO_SYMPTOMS) {
      symptomOther = NO_SYMPTOMS;
    } else if (inputs.symptomOther && inputs.symptomOther.length) {
      symptomOther = inputs.symptomOther.trim();
    }

    dispatch(
      setQuestionnaireAnswer({
        key: HOT_FLASHES_TREATMENT_KEYS.SELECT_SYMPTOMS,
        index: questionnaire?.index || '',
        data: {
          selectedOptions: dataSubmit || [],
          symptomOther,
          selectedSymptomOther: inputs.selectedSymptomOther,
        },
      }),
    );
    if (inputs.selectedSymptomOther === NO_SYMPTOMS) {
      dispatch(setNextQuestionnaire({value: HOT_FLASHES_TREATMENT_KEYS.HAVING_REGULAR_PERIODS}));
      return;
    }
    dispatch(setNextQuestionnaire({value: HOT_FLASHES_TREATMENT_KEYS.DESCRIBE_CURRENT_SYMPTOMS}));
  };

  const renderSymptomInput = () => {
    if (inputs.selectedSymptomOther === ENTER_MY_OWN) {
      return (
        <UiBox w="100%" mt={spacing.small} className="animate__animated animate__fadeInLeft">
          <UiFormControl id="symptomOther" isRequired isInvalid={!!validation?.errors?.symptomOther}>
            <UiTextarea
              bg="white"
              borderRadius={spacing.tiny}
              placeholder="Type your response here..."
              defaultValue={inputs.symptomOther}
              onChange={({target}) => setInputs({symptomOther: target.value})}
              minH="120px"
              maxLength={300}
            />
            {validation?.errors?.symptomOther && (
              <UiFormErrorMessage>{validation?.errors?.symptomOther[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiBox>
      );
    }
    return null;
  };

  return (
    <>
      <UiQuestionnaireLayout
        backIcon
        disableNext={!isValid}
        handleOnNext={onClickSubmit}
        actions={questionnaire?.actions}
        checkoutStep={questionnaire?.checkoutStep}
      >
        <Col alignItems="center" textAlign="center">
          <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
            {questionnaire?.questions?.default}
          </UiText>
          <UiText fontSize="sm" fontWeight="bold" color="liteOuter" mt={spacing.tiny}>
            {questionnaire?.questions?.sub}
          </UiText>
          <UiSimpleGrid columns={3} spacing={{base: 2, lg: 5}} mt={spacing.small}>
            {questionnaire?.options?.map(value => (
              <UiBox key={`${value.label}-${value.value}`}>
                <UiToggleButton
                  selected={inputs?.defaultOptions?.[value.value] || false}
                  value={value}
                  onClick={() => onClickOption(value)}
                  labelFontSize={12}
                />
              </UiBox>
            ))}
          </UiSimpleGrid>
          <UiSimpleGrid columns={2} spacing={5} mt={spacing.small}>
            {questionnaire?.symptomOther?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.key}`}
                title={value.label}
                variant={inputs.selectedSymptomOther === value.key ? 'solid' : 'toggle'}
                onClick={() => onClickSelectedSymptomOther(value)}
              />
            ))}
          </UiSimpleGrid>
          {renderSymptomInput()}
        </Col>
      </UiQuestionnaireLayout>
    </>
  );
};

export default Questionnaire4Screen;
